import type { SubmitEnpsInput } from '../types';

// Spec UC-01 exception: "the save fails — the response is not lost and the
// user is not interrupted". Every answer is written here before it is sent and
// removed once the backend has accepted (or definitively rejected) it, so a
// failed request or a closed tab is retried later. The backend `submit` is
// idempotent per slot, so a duplicate send is harmless.

const STORAGE_KEY = 'investbanq-enps-pending-submissions';

// Free-text comments must not linger on a shared machine: anything not
// delivered within this window is dropped, whoever it belongs to.
export const PENDING_SUBMISSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface PendingSubmission {
  userId: string;
  input: SubmitEnpsInput;
  queuedAt: number;
}

function sameSlot(a: PendingSubmission, b: PendingSubmission): boolean {
  return (
    a.userId === b.userId && a.input.year === b.input.year && a.input.quarter === b.input.quarter
  );
}

function isPendingSubmission(value: unknown): value is PendingSubmission {
  if (!value || typeof value !== 'object') return false;
  const { userId, input, queuedAt } = value as Partial<PendingSubmission>;
  return (
    typeof userId === 'string' &&
    typeof queuedAt === 'number' &&
    !!input &&
    typeof input.year === 'number' &&
    typeof input.quarter === 'number' &&
    typeof input.score === 'number'
  );
}

function writePendingSubmissions(items: PendingSubmission[]): void {
  try {
    if (items.length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage unavailable (private mode, quota) — the in-flight request is
    // still sent; only the retry-later safety net is lost.
  }
}

/** Live (non-expired) queued answers; expired ones are pruned on read. */
export function readPendingSubmissions(now: number = Date.now()): PendingSubmission[] {
  let stored: PendingSubmission[];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    stored = Array.isArray(parsed) ? parsed.filter(isPendingSubmission) : [];
  } catch {
    return [];
  }
  const live = stored.filter((item) => now - item.queuedAt < PENDING_SUBMISSION_TTL_MS);
  if (live.length !== stored.length) writePendingSubmissions(live);
  return live;
}

export function enqueuePendingSubmission(item: PendingSubmission): void {
  const rest = readPendingSubmissions().filter((existing) => !sameSlot(existing, item));
  writePendingSubmissions([...rest, item]);
}

export function removePendingSubmission(item: PendingSubmission): void {
  writePendingSubmissions(readPendingSubmissions().filter((existing) => !sameSlot(existing, item)));
}

export function hasPendingSubmission(userId: string, year: number, quarter: number): boolean {
  return readPendingSubmissions().some(
    (item) => item.userId === userId && item.input.year === year && item.input.quarter === quarter,
  );
}
